import React from "react";
import { Form, Input, Alert, theme, Row, Col } from "antd";
import { useResumeStore } from "../../store/resumeStore";
import PhotoUpload from "./PhotoUpload";
import { FIELD_SPELLCHECK_PROPS } from "./resumeQuality";
import { getSelectedResumeType } from "./resumeValidation";

const { TextArea } = Input;
const { useToken } = theme;

const SUMMARY_SOFT = 600;
const SUMMARY_MAX = 800;

const OBJECTIVE_SOFT = 200;
const OBJECTIVE_MAX = 300;

export default function IntroEditor() {
  const { token } = useToken();
  const [form] = Form.useForm();

  const resume = useResumeStore((s) => s.resume);
  const updateResume = useResumeStore((s) => s.updateResume);

  const labelStyle = {
    color: token.colorTextHeading,
    fontWeight: 500,
  };

  const summaryLength = resume.summary?.length || 0;
  const objectiveLength = resume.objective?.length || 0;
  const selectedResumeType = getSelectedResumeType();
  const isFresherResume = selectedResumeType === "fresher";

  const updateField = (field, value) => {
    updateResume(field, value);
  };

  /* ---------------- VALIDATORS ---------------- */

  const noTripleRepeat = (_, value) => {
    if (!value) return Promise.reject(new Error(""));

    const repeatRegex = /(.)\1\1/;

    if (repeatRegex.test(value)) {
      return Promise.reject(
        new Error("Avoid repeating the same character 3 times")
      );
    }

    return Promise.resolve();
  };

  const emailValidator = (_, value) => {
    if (!value) return Promise.reject(new Error("Email is required"));

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      return Promise.reject(new Error("Enter a valid email"));
    }

    return Promise.resolve();
  };

  const phoneValidator = (_, value) => {
    if (!value) return Promise.reject(new Error("Phone number is required"));

    const phoneRegex = /^[6-9]\d{9}$/;

    if (!phoneRegex.test(value)) {
      return Promise.reject(
        new Error("Enter a valid 10 digit mobile number")
      );
    }

    return Promise.resolve();
  };

  return (
    <Form layout="vertical" form={form} initialValues={resume}>

      {/* NAME + ROLE */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Form.Item
            name="name"
            label={<span style={labelStyle}>Full Name</span>}
            rules={[
              { required: true, message: "Name is required" },
              { validator: noTripleRepeat },
            ]}
          >
            <Input
              value={resume.name}
              onChange={(e) => updateField("name", e.target.value)}
              {...FIELD_SPELLCHECK_PROPS}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name="roleTitle"
            label={<span style={labelStyle}>Role / Title</span>}
            rules={[
              { required: true, message: "Role is required" },
              { validator: noTripleRepeat },
            ]}
          >
            <Input
              value={resume.roleTitle || resume.role}
              onChange={(e) => {
                updateField("roleTitle", e.target.value);
                updateField("role", e.target.value);
              }}
              {...FIELD_SPELLCHECK_PROPS}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* EMAIL + PHONE */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Form.Item
            name="email"
            label={<span style={labelStyle}>Email</span>}
            rules={[{ validator: emailValidator }]}
          >
            <Input
              value={resume.email}
              onChange={(e) => updateField("email", e.target.value)}
              spellCheck={false}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name="phone"
            label={<span style={labelStyle}>Phone</span>}
            rules={[{ validator: phoneValidator }]}
          >
            <Input
              value={resume.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              spellCheck={false}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* LOCATION + EXPERIENCE */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Form.Item
            name="location"
            label={<span style={labelStyle}>Location</span>}
            rules={[
              { required: true, message: "Location is required" },
              { validator: noTripleRepeat },
            ]}
          >
            <Input
              value={resume.location}
              onChange={(e) => updateField("location", e.target.value)}
              {...FIELD_SPELLCHECK_PROPS}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name="relevantExperience"
            label={
              <span style={labelStyle}>Relevant Experience (Years)</span>
            }
            rules={
              isFresherResume
                ? []
                : [{ required: true, message: "Experience is required" }]
            }
          >
            <Input
              type="number"
              value={resume.relevantExperience}
              onChange={(e) =>
                updateField("relevantExperience", e.target.value)
              }
            />
          </Form.Item>
        </Col>
      </Row>

      {/* PHOTO + TOTAL EXPERIENCE */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Form.Item
            label={<span style={labelStyle}>Profile Photo (Optional)</span>}
          >
            <PhotoUpload resume={resume} updateField={updateField} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name="totalExperience"
            label={
              <span style={labelStyle}>Total Experience (Years)</span>
            }
            rules={
              isFresherResume
                ? []
                : [{ required: true, message: "Total experience required" }]
            }
          >
            <Input
              type="number"
              value={resume.totalExperience}
              onChange={(e) =>
                updateField("totalExperience", e.target.value)
              }
            />
          </Form.Item>
        </Col>
      </Row>

      {/* SUMMARY */}
      <Form.Item
        name="summary"
        label={<span style={labelStyle}>About Me</span>}
        rules={[
          { required: true, message: "Summary is required" },
          { validator: noTripleRepeat },
        ]}
        extra={`${summaryLength}/${SUMMARY_MAX} characters`}
      >
        <TextArea
          rows={5}
          maxLength={SUMMARY_MAX}
          value={resume.summary}
          onChange={(e) => updateField("summary", e.target.value)}
          {...FIELD_SPELLCHECK_PROPS}
        />
      </Form.Item>

      {summaryLength > SUMMARY_SOFT && summaryLength <= SUMMARY_MAX && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Summary is getting long"
        />
      )}

      {summaryLength >= SUMMARY_MAX && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Summary too long"
        />
      )}

      {/* OBJECTIVE */}
      <Form.Item
        name="objective"
        label={<span style={labelStyle}>Career Objective</span>}
        rules={[
          { required: true, message: "Objective is required" },
          { validator: noTripleRepeat },
        ]}
        extra={`${objectiveLength}/${OBJECTIVE_MAX} characters`}
      >
        <TextArea
          rows={3}
          maxLength={OBJECTIVE_MAX}
          value={resume.objective}
          onChange={(e) => updateField("objective", e.target.value)}
          {...FIELD_SPELLCHECK_PROPS}
        />
      </Form.Item>

      {objectiveLength > OBJECTIVE_SOFT &&
        objectiveLength <= OBJECTIVE_MAX && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Objective should be short"
          />
        )}

      {objectiveLength >= OBJECTIVE_MAX && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Objective too long"
        />
      )}
    </Form>
  );
}
